
import { defineComponent, nextTick } from 'vue'
import {
  ExclamationCircleOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  ClockCircleOutlined
} from '@ant-design/icons-vue'
import { notification, message } from 'ant-design-vue'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'

import { EnvelopeService } from '@/services/EnvelopeService'
import { ListEnvelope } from '@/services/responses/ListEnvelopeResponse'

interface Signatory {
  id?: number | string
  partName?: string
  email?: string
  phone?: string
  partSigned?: boolean
  status?: number
  signedAt?: string | null
  signedDate?: string | null
  dateSigned?: string | null
  signatureDate?: string | null
  signatureDateTime?: string | null
  dateSignature?: string | null
  [key: string]: any
}

export default defineComponent({
  name: 'CancelEnvelopeModal',

  components: {
    ExclamationCircleOutlined,
    FilePdfOutlined,
    CheckCircleFilled,
    ClockCircleOutlined
  },

  props: {
    visible: {
      type: Boolean,
      required: true
    },

    envelope: {
      type: Object as () => ListEnvelope | null,
      default: null
    }
  },

  emits: ['update:visible', 'canceled'],

  data () {
    return {
      isLoadingDetails: false,
      isCancelling: false,
      signatories: [] as Signatory[],
      requestId: 0,
      confirmationSeconds: 5,
      confirmationTimer: null as number | null
    }
  },

  computed: {
    formattedCreationDate (): string {
      const date = this.envelope?.date

      if (!date) {
        return 'Data não informada'
      }

      const parsedDate = dayjs(date)

      return parsedDate.isValid()
        ? parsedDate.format('DD/MM/YYYY')
        : String(date)
    },

    progressPercentage (): number {
      const start = Number(this.envelope?.startProgress ?? 0)
      const end = Number(this.envelope?.endProgress ?? 0)

      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= 0) {
        return 0
      }

      return Math.min(100, Math.max(0, (start / end) * 100))
    }
  },

  watch: {
    visible (isVisible: boolean) {
      if (isVisible) {
        this.loadSignatories()
        nextTick(() => this.focusBackButton())
      } else {
        this.requestId++
        this.clearConfirmationCountdown()
        this.confirmationSeconds = 5
        this.isLoadingDetails = false
        this.isCancelling = false
      }
    },

    'envelope.hashId' () {
      if (this.visible) {
        this.loadSignatories()
      }
    }
  },

  methods: {
    async loadSignatories () {
      if (!this.envelope?.hashId) {
        this.signatories = []
        return
      }

      const currentRequest = ++this.requestId
      this.isLoadingDetails = true
      this.signatories = []

      try {
        const details = await EnvelopeService
          .getInstance()
          .detailsEnvelope(this.envelope.hashId)

        // Ignora respostas de requisições anteriores.
        if (
          currentRequest !== this.requestId ||
          !this.visible
        ) {
          return
        }

        this.signatories = Array.isArray(details?.signatureRequests)
          ? details.signatureRequests
          : []
      } catch (error) {
        if (
          currentRequest !== this.requestId ||
          !this.visible
        ) {
          return
        }

        notification.error({
          message: 'Erro ao carregar o documento',
          description:
            'Não foi possível carregar os signatários. Tente novamente.'
        })
      } finally {
        if (
          currentRequest === this.requestId &&
          this.visible
        ) {
          this.isLoadingDetails = false
          this.startConfirmationCountdown()
        }
      }
    },

    isSigned (signatory: Signatory): boolean {
        return signatory.status === 2
    },

    getInitials (name?: string): string {
      if (!name?.trim()) {
        return '?'
      }

      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
    },

    getSignatureDate (signatory: Signatory): string {
      // Os nomes alternativos permitem compatibilidade com
      // diferentes DTOs. Confirmaremos o campo real na API.
      const value =
        signatory.signedAt ||
        signatory.signedDate ||
        signatory.dateSigned ||
        signatory.signatureDateTime ||
        signatory.signatureDate ||
        signatory.dateSignature

      if (!value) {
        return 'Data não informada'
      }

      const parsedDate = dayjs(value)

      return parsedDate.isValid()
        ? parsedDate.format('DD/MM/YYYY HH:mm')
        : 'Data não informada'
    },

    focusBackButton () {
      const button = this.$refs.backButton as any

      if (button?.$el instanceof HTMLElement) {
        button.$el.focus()
      } else if (button instanceof HTMLElement) {
        button.focus()
      }
    },

    closeModal () {
      if (this.isCancelling) {
        return
      }

      this.$emit('update:visible', false)
    },

    async confirmCancellation () {
      if (
        !this.envelope ||
        this.isCancelling ||
        this.isLoadingDetails
      ) {
        return
      }

      this.isCancelling = true

      try {
        await EnvelopeService
          .getInstance()
          .cancelEnvelope(this.envelope.id)

        notification.success({
            message: 'Documento cancelado com sucesso',
            placement: 'topRight',
            duration: 4,
            class: 'notification-success-green'
        })

        // Permite que a tela atualize as listas e remova
        // o documento das assinaturas pendentes.
        this.$emit('canceled', this.envelope)

        this.$emit('update:visible', false)
      } catch (error) {
        notification.error({
          message: 'Falha ao cancelar documento',
          description:
            'Não foi possível cancelar o documento. Tente novamente.'
        })
      } finally {
        this.isCancelling = false
      }
    },
    startConfirmationCountdown () {
      this.clearConfirmationCountdown()
      this.confirmationSeconds = 5

      this.confirmationTimer = setInterval(() => {
        if (this.confirmationSeconds <= 1) {
          this.confirmationSeconds = 0
          this.clearConfirmationCountdown()
          return
        }

        this.confirmationSeconds--
      }, 1000) as unknown as number
    },
    clearConfirmationCountdown () {
      if (this.confirmationTimer !== null) {
        clearInterval(this.confirmationTimer)
        this.confirmationTimer = null
      }
    }
  }
})
